import { useState, useEffect, useMemo, useCallback } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { formatDateTime } from '@/shared/utils/dates';

/**
 * Debug Screenshots Gallery
 * 
 * Displays E2E test screenshots for QA review.
 * 
 * Features:
 * - Search/filter by name
 * - Category filtering
 * - Desktop/Mobile comparison view
 * - Refresh without reload
 * - Timestamp display
 */

interface Screenshot {
  name: string;
  path: string;
  category: string;
}

interface Manifest {
  generated: string;
  count: number;
  screenshots: Screenshot[];
}

function ErrorFallback({ error, resetErrorBoundary }: { error: unknown; resetErrorBoundary: () => void }) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-red-500 mb-4">Something went wrong</h1>
        <pre className="bg-slate-900 p-4 rounded text-left text-sm text-red-400 mb-4 max-w-lg overflow-auto">
          {errorMessage}
        </pre>
        <button
          onClick={resetErrorBoundary}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

export function DebugScreenshots() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <DebugScreenshotsContent />
    </ErrorBoundary>
  );
}

function DebugScreenshotsContent() {
  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [modalImage, setModalImage] = useState<Screenshot | null>(null);
  const [compareMode, setCompareMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadScreenshots = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      // Add cache buster for refresh
      const cacheBuster = isRefresh ? `?t=${Date.now()}` : '';
      const res = await fetch(`/screenshots/manifest.json${cacheBuster}`);
      if (res.ok) {
        const data: Manifest = await res.json();
        setScreenshots(Array.isArray(data?.screenshots) ? data.screenshots : []);
        setGeneratedAt(data?.generated || null);
        setError(null);
      } else {
        setScreenshots([]);
      }
    } catch (err) {
      console.error('Failed to load screenshots:', err);
      setError(err instanceof Error ? err.message : 'Failed to load screenshots');
      setScreenshots([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadScreenshots();
  }, [loadScreenshots]);

  const categories = useMemo(() => {
    const cats = new Set(screenshots.map(s => s.category));
    return ['all', ...Array.from(cats)];
  }, [screenshots]);

  // Filter by category and search query
  const filtered = useMemo(() => {
    let result = screenshots;
    
    if (selectedCategory !== 'all') {
      result = result.filter(s => s.category === selectedCategory);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(query));
    }
    
    return result;
  }, [screenshots, selectedCategory, searchQuery]);

  // Group by category
  const grouped = useMemo(() => {
    return filtered.reduce((acc, s) => {
      if (!acc[s.category]) acc[s.category] = [];
      acc[s.category].push(s);
      return acc;
    }, {} as Record<string, Screenshot[]>);
  }, [filtered]);

  // Find comparison pairs (desktop/mobile of same screen)
  const comparisonPairs = useMemo(() => {
    if (!compareMode) return [];
    
    const pairs: { name: string; desktop?: Screenshot; mobile?: Screenshot }[] = [];
    const seen = new Set<string>();
    
    for (const shot of screenshots) {
      // Extract base name without viewport suffix
      const baseName = shot.name
        .replace(/-desktop$/, '')
        .replace(/-mobile$/, '')
        .replace(/-(desktop|mobile)\.png$/, '');
      
      if (seen.has(baseName)) continue;
      
      const desktop = screenshots.find(s => 
        s.path.includes('/desktop/') && s.name.includes(baseName)
      );
      const mobile = screenshots.find(s => 
        s.path.includes('/mobile/') && s.name.includes(baseName)
      );
      
      if (desktop || mobile) {
        pairs.push({ name: baseName, desktop, mobile });
        seen.add(baseName);
      }
    }
    
    return pairs;
  }, [screenshots, compareMode]);

  // Format timestamp
  const formattedTime = useMemo(() => {
    if (!generatedAt) return null;
    try {
      const date = new Date(generatedAt);
      return formatDateTime(date);
    } catch {
      return generatedAt;
    }
  }, [generatedAt]);

  // Keyboard navigation in modal
  useEffect(() => {
    if (!modalImage) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setModalImage(null);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const currentIndex = filtered.findIndex(s => s.path === modalImage.path);
        if (currentIndex === -1) return;
        
        const newIndex = e.key === 'ArrowLeft' 
          ? (currentIndex - 1 + filtered.length) % filtered.length
          : (currentIndex + 1) % filtered.length;
        
        setModalImage(filtered[newIndex]);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalImage, filtered]);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-950/90 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-bold text-green-500">UX Screenshots</h1>
              {formattedTime && (
                <p className="text-xs text-slate-500">Generated: {formattedTime}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadScreenshots(true)}
                disabled={refreshing}
                className="px-3 py-1.5 text-sm bg-slate-800 text-slate-300 rounded hover:bg-slate-700 disabled:opacity-50 flex items-center gap-1"
              >
                {refreshing ? (
                  <span className="animate-spin">↻</span>
                ) : (
                  '↻'
                )}
                Refresh
              </button>
              <Link 
                to="/debug" 
                className="px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
              >
                ← Debug
              </Link>
            </div>
          </div>
          
          {/* Search and controls */}
          {screenshots.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 pb-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <input
                  type="text"
                  placeholder="Search screenshots..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-1.5 pl-8 text-sm bg-slate-800 border border-slate-700 rounded text-white placeholder-slate-500 focus:outline-none focus:border-green-500"
                />
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500">
                  <MagnifyingGlass size={14} weight="bold" />
                </span>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                  >
                    ×
                  </button>
                )}
              </div>
              
              {/* Compare toggle */}
              <button
                onClick={() => setCompareMode(!compareMode)}
                className={`px-3 py-1.5 text-sm rounded whitespace-nowrap ${
                  compareMode
                    ? 'bg-green-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                Compare
              </button>
            </div>
          )}
          
          {/* Category Filter */}
          {screenshots.length > 0 && !compareMode && (
            <div className="flex gap-2 overflow-x-auto pb-3">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1 text-sm rounded-full whitespace-nowrap transition-colors ${
                    selectedCategory === cat
                      ? 'bg-green-600 text-white'
                      : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                  }`}
                >
                  {cat === 'all' ? 'All' : cat}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {error ? (
          <div className="text-center py-20">
            <h2 className="text-xl font-semibold mb-2 text-red-500">Error Loading Screenshots</h2>
            <p className="text-slate-500 mb-4">{error}</p>
            <button
              onClick={() => loadScreenshots()}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Retry
            </button>
          </div>
        ) : loading ? (
          <div className="text-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-slate-500">Loading screenshots...</p>
          </div>
        ) : screenshots.length === 0 ? (
          <div className="text-center py-20">
            <h2 className="text-xl font-semibold mb-2">No Screenshots Found</h2>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Run E2E tests to capture screenshots. They'll appear here automatically.
            </p>
            <div className="bg-slate-900 rounded-lg p-4 max-w-lg mx-auto text-left">
              <p className="text-sm text-slate-400 mb-2">Run this command:</p>
              <code className="block text-sm text-green-400">
                npm run test:e2e:screenshots
              </code>
            </div>
          </div>
        ) : compareMode ? (
          /* Comparison View */
          <div className="space-y-8">
            <div className="text-center text-sm text-slate-500 mb-4">
              Showing {comparisonPairs.length} comparison pairs
            </div>
            {comparisonPairs.map(pair => (
              <div key={pair.name} className="bg-slate-900 rounded-lg p-4">
                <h3 className="text-sm font-medium text-slate-300 mb-3">{pair.name}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Desktop</div>
                    {pair.desktop ? (
                      <button
                        onClick={() => setModalImage(pair.desktop!)}
                        className="w-full aspect-video bg-slate-800 rounded overflow-hidden hover:ring-2 hover:ring-green-500"
                      >
                        <img
                          src={pair.desktop.path}
                          alt={pair.desktop.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </button>
                    ) : (
                      <div className="w-full aspect-video bg-slate-800 rounded flex items-center justify-center text-slate-600">
                        No desktop screenshot
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Mobile</div>
                    {pair.mobile ? (
                      <button
                        onClick={() => setModalImage(pair.mobile!)}
                        className="w-full aspect-video bg-slate-800 rounded overflow-hidden hover:ring-2 hover:ring-green-500"
                      >
                        <img
                          src={pair.mobile.path}
                          alt={pair.mobile.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </button>
                    ) : (
                      <div className="w-full aspect-video bg-slate-800 rounded flex items-center justify-center text-slate-600">
                        No mobile screenshot
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {comparisonPairs.length === 0 && (
              <div className="text-center py-10 text-slate-500">
                No desktop/mobile pairs found. Comparison works when you have screenshots in both /desktop/ and /mobile/ folders with matching names.
              </div>
            )}
          </div>
        ) : (
          /* Grid View */
          <>
            {/* Stats */}
            <div className="flex gap-6 mb-6 text-center">
              <div>
                <div className="text-2xl font-bold text-green-500">{filtered.length}</div>
                <div className="text-xs text-slate-500">
                  {searchQuery ? 'Matches' : 'Screenshots'}
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-500">{categories.length - 1}</div>
                <div className="text-xs text-slate-500">Categories</div>
              </div>
            </div>

            {/* No results */}
            {filtered.length === 0 && searchQuery && (
              <div className="text-center py-10 text-slate-500">
                No screenshots matching "{searchQuery}"
              </div>
            )}

            {/* Grid by Category */}
            {Object.entries(grouped).map(([category, shots]) => (
              <section key={category} className="mb-10">
                <h2 className="text-lg font-semibold text-green-500 border-b border-slate-800 pb-2 mb-4">
                  {category} <span className="text-slate-600 font-normal">({shots.length})</span>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {shots.map((shot) => (
                    <button
                      key={shot.path}
                      onClick={() => setModalImage(shot)}
                      className="bg-slate-900 rounded-lg overflow-hidden hover:ring-2 hover:ring-green-500 transition-all group"
                    >
                      <img
                        src={shot.path}
                        alt={shot.name}
                        loading="lazy"
                        className="w-full h-32 object-cover object-top group-hover:scale-105 transition-transform"
                      />
                      <div className="p-2 text-xs text-slate-400 truncate">
                        {shot.name}
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </main>

      {/* Modal */}
      {modalImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setModalImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-3xl text-white/60 hover:text-white"
            onClick={() => setModalImage(null)}
          >
            ×
          </button>
          <div className="max-w-5xl max-h-[90vh] flex flex-col items-center">
            <img
              src={modalImage.path}
              alt={modalImage.name}
              className="max-h-[80vh] rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="mt-4 text-lg text-white">{modalImage.name}</p>
            <p className="text-sm text-slate-500">{modalImage.category}</p>
            <p className="text-xs text-slate-600 mt-1">← → to navigate • ESC to close</p>
          </div>
        </div>
      )}
    </div>
  );
}
